import { FormControl, FormGroup } from '@angular/forms';
import {
  COMPATIBILITY_TOGGLE_QUESTIONNAIRE_IDS,
  disabledQuestionnaireIdsForSelection,
  INTAKE_STANDARD_KEY,
  isMultiSendRestrictionActive,
  resolveCompatibleQuestionnaireSelection,
  selectedIdMap,
  selectedIdsFromValues
} from 'src/app/core/api/questionnaire-selection.util';
import {
  OneClickQRESetItem,
  OneClickQRESetItemKind
} from 'src/app/core/models/one-click-qre-set.model';

export interface QuestionnaireLabelMaps {
  standardKeyToName: { [id: string]: string };
  customKeyToName: { [id: string]: string };
  consentKeyToName: { [id: string]: string };
  intakeKeyToName: { [id: string]: string };
  pampTodoKeyToName: { [id: string]: string };
}

export function addControlsIfMissing(formGroup: FormGroup, keys: string[]): void {
  if (!formGroup) {
    return;
  }
  keys.forEach(key => {
    if (key && !formGroup.controls[key]) {
      formGroup.addControl(key, new FormControl(false));
    }
  });
}

export function valuesByKeyFromFormGroup(formGroup: FormGroup): { [key: string]: boolean } {
  const values: { [key: string]: boolean } = {};
  if (!formGroup) {
    return values;
  }
  Object.keys(formGroup.controls).forEach(key => {
    values[key] = formGroup.controls[key].value === true;
  });
  return values;
}

export function selectedIdsFromFormGroup(formGroup: FormGroup): string[] {
  return selectedIdsFromValues(valuesByKeyFromFormGroup(formGroup));
}

export function setAllFormGroupValues(formGroup: FormGroup, checked: boolean): void {
  if (!formGroup) {
    return;
  }
  Object.keys(formGroup.controls).forEach(key => {
    formGroup.controls[key].enable({ emitEvent: false });
    formGroup.controls[key].setValue(checked, { emitEvent: false });
  });
}

export function setFormGroupSelectedIds(formGroup: FormGroup, selectedIds: { [id: string]: boolean }): void {
  if (!formGroup) {
    return;
  }
  Object.keys(formGroup.controls).forEach(key => {
    formGroup.controls[key].enable({ emitEvent: false });
    formGroup.controls[key].setValue(!!selectedIds[key], { emitEvent: false });
  });
}

export function applyCompatibilityToFormGroup(
  formGroup: FormGroup,
  intakeFormsEnabled: boolean,
  preferredIds?: string[]
): boolean {
  if (!formGroup) {
    return false;
  }

  const compatibleIds = selectedIdMap(
    resolveCompatibleQuestionnaireSelection(
      selectedIdsFromFormGroup(formGroup),
      intakeFormsEnabled,
      preferredIds
    )
  );
  Object.keys(formGroup.controls).forEach(id => {
    if (formGroup.controls[id].value === true && !compatibleIds[id]) {
      formGroup.controls[id].setValue(false, { emitEvent: false });
    }
  });

  const selectedIds = selectedIdsFromFormGroup(formGroup);
  const disabledIds = disabledQuestionnaireIdsForSelection(selectedIds, intakeFormsEnabled);

  const toggleIds = COMPATIBILITY_TOGGLE_QUESTIONNAIRE_IDS.concat(
    intakeFormsEnabled ? [INTAKE_STANDARD_KEY] : []
  );

  toggleIds.forEach(id => {
    const control = formGroup.controls[id];
    if (!control) {
      return;
    }
    control.enable({ emitEvent: false });
  });

  disabledIds.forEach(id => {
    const control = formGroup.controls[id];
    if (!control) {
      return;
    }
    control.setValue(false, { emitEvent: false });
    control.disable({ emitEvent: false });
  });

  return isMultiSendRestrictionActive(selectedIdsFromFormGroup(formGroup), intakeFormsEnabled);
}

export function itemKindForQuestionnaireId(
  id: string,
  maps: QuestionnaireLabelMaps
): OneClickQRESetItemKind {
  if (maps.consentKeyToName && maps.consentKeyToName[id]) {
    return 'consent';
  }
  if (maps.customKeyToName && maps.customKeyToName[id]) {
    return 'custom';
  }
  if (maps.intakeKeyToName && maps.intakeKeyToName[id]) {
    return 'intake';
  }
  return 'standard';
}

export function labelForQuestionnaireId(id: string, maps: QuestionnaireLabelMaps): string {
  if (maps.standardKeyToName && maps.standardKeyToName[id]) {
    return maps.standardKeyToName[id];
  }
  if (maps.customKeyToName && maps.customKeyToName[id]) {
    return maps.customKeyToName[id];
  }
  if (maps.consentKeyToName && maps.consentKeyToName[id]) {
    return maps.consentKeyToName[id];
  }
  if (maps.intakeKeyToName && maps.intakeKeyToName[id]) {
    return maps.intakeKeyToName[id];
  }
  if (maps.pampTodoKeyToName && maps.pampTodoKeyToName[id]) {
    return maps.pampTodoKeyToName[id];
  }
  return id;
}

export function collectSelectedSetItems(
  questionnaireTypesFormGroup: FormGroup,
  pampTodosFormGroup: FormGroup,
  maps: QuestionnaireLabelMaps
): OneClickQRESetItem[] {
  const items: OneClickQRESetItem[] = [];
  selectedIdsFromFormGroup(questionnaireTypesFormGroup).forEach(id => {
    items.push({
      kind: itemKindForQuestionnaireId(id, maps),
      id: id,
      label: labelForQuestionnaireId(id, maps)
    });
  });
  selectedIdsFromFormGroup(pampTodosFormGroup).forEach(id => {
    items.push({
      kind: 'pampTodo',
      id: id,
      label: (maps.pampTodoKeyToName && maps.pampTodoKeyToName[id]) || id
    });
  });
  return items;
}
